import {
  ChangeEvent,
  DynamicContentType,
  FileEntryBM,
  LucideIconName,
  TopSectionData,
} from '@bm-js/atlascloud-shared'
import Input from './Input'
import Textarea from './Textarea'
import RightBarGroup from './RightBarGroup'
import Select from './Select'
import {
  Button,
  Icon,
  marginOptions,
  ResponsiveImage,
} from '@bm-js/atlascloud-ui'
import { TextAlignmentOptions } from './TypographyOptions'
import { useMedia } from '../utils/hooks'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

type Props = {
  setData: (e: ChangeEvent<string | FileEntryBM[] | undefined>) => void
  content: DynamicContentType<TopSectionData>
}

const TopSectionEditor = ({ setData, content }: Props) => {
  const { closeMediaSelector, openMediaSelector } = useMedia()
  const { tr } = useTr()

  return (
    <>
      <Input
        light
        label={tr(TrKey.topSectionEditorHeaderTextLabel)}
        desc={tr(TrKey.topSectionEditorHeaderTextDescription)}
        placeholder={tr(TrKey.topSectionEditorHeaderTextPlaceholder)}
        type='text'
        name='headerText'
        id='headerText'
        value={content.data.headerText}
        onChange={setData}
      />
      <RightBarGroup title='Accent' topLine>
        <Input
          light
          label={tr(TrKey.topSectionEditorAccentLabel)}
          desc={tr(TrKey.topSectionEditorAccentDescription)}
          placeholder={tr(TrKey.topSectionEditorAccentPlaceholder)}
          type='text'
          name='headerHighlight'
          id='headerHighlight'
          value={content.data.headerHighlight || ''}
          onChange={setData}
        />
      </RightBarGroup>
      <Textarea
        light
        label={tr(TrKey.topSectionEditorParagraphTextLabel)}
        desc={tr(TrKey.topSectionEditorParagraphTextDescription)}
        placeholder={tr(TrKey.topSectionEditorParagraphTextPlaceholder)}
        name='paragraphText'
        id='paragraphText'
        value={content.data.paragraphText}
        onChange={setData}
      />
      <Input
        light
        label={tr(TrKey.topSectionEditorCtaTextLabel)}
        desc={tr(TrKey.topSectionEditorCtaTextDescription)}
        placeholder={tr(TrKey.topSectionEditorCtaTextPlaceholder)}
        type='text'
        name='ctaText'
        id='ctaText'
        value={content.data.ctaText}
        onChange={setData}
      />
      <Input
        light
        label={tr(TrKey.topSectionEditorCtaLinkLabel)}
        desc={tr(TrKey.topSectionEditorCtaLinkDescription)}
        placeholder={tr(TrKey.topSectionEditorCtaLinkPlaceholder)}
        type='text'
        name='ctaLink'
        id='ctaLink'
        value={content.data.ctaLink}
        onChange={setData}
      />
      <Select
        light
        label={tr(TrKey.topSectionEditorCtaColorLabel)}
        desc={tr(TrKey.topSectionEditorCtaColorDescription)}
        id='ctaColor'
        name='ctaColor'
        value={content.data.ctaColor || 'primary'}
        onChange={setData}
      >
        <option value='primary'>
          {tr(TrKey.topSectionCtaColorOptionPrimary)}
        </option>
        <option value='accent'>
          {tr(TrKey.topSectionCtaColorOptionAccent)}
        </option>
      </Select>
      <TextAlignmentOptions
        omit={['right', 'center-mobile']}
        content={content}
        setData={setData}
      />
      <RightBarGroup title={tr(TrKey.topSectionEditorOverlayTitle)} topLine>
        <Select
          light
          label={tr(TrKey.topSectionEditorOverlayColorLabel)}
          desc={tr(TrKey.topSectionEditorOverlayColorDescription)}
          id='overlayColor'
          name='overlayColor'
          value={content.data.overlayColor}
          onChange={setData}
        >
          <option value='' disabled>
            {tr(TrKey.topSectionEditorOverlayColorPlaceholder)}
          </option>
          <option value='white'>
            {tr(TrKey.topSectionOverlayColorOptionWhite)}
          </option>
          <option value='gray200'>
            {tr(TrKey.topSectionOverlayColorOptionGray200)}
          </option>
          <option value='gray400'>
            {tr(TrKey.topSectionOverlayColorOptionGray400)}
          </option>
          <option value='gray600'>
            {tr(TrKey.topSectionOverlayColorOptionGray600)}
          </option>
          <option value='black'>
            {tr(TrKey.topSectionOverlayColorOptionBlack)}
          </option>
        </Select>
        <Select
          light
          label={tr(TrKey.topSectionEditorOverlayStrengthLabel)}
          desc={tr(TrKey.topSectionEditorOverlayStrengthDescription)}
          id='overlayStrength'
          name='overlayStrength'
          value={content.data.overlayStrength}
          onChange={setData}
        >
          <option value='' disabled>
            {tr(TrKey.topSectionEditorOverlayStrengthPlaceholder)}
          </option>
          <option value='0.2'>
            {tr(TrKey.topSectionOverlayStrengthOption02)}
          </option>
          <option value='0.5'>
            {tr(TrKey.topSectionOverlayStrengthOption05)}
          </option>
          <option value='0.7'>
            {tr(TrKey.topSectionOverlayStrengthOption07)}
          </option>
          <option value='1'>
            {tr(TrKey.topSectionOverlayStrengthOption1)}
          </option>
        </Select>
      </RightBarGroup>
      <RightBarGroup title={tr(TrKey.topSectionEditorImageCarouselTitle)}>
        <p className='m0 label light'>
          {tr(TrKey.topSectionEditorImageCarouselTitle)}
        </p>
        <p className='m0 desc light'>
          {tr(TrKey.topSectionEditorImageCarouselDescription)}
        </p>
        {content.data.images.map((image, i) => (
          <div
            className='top-section-image-selector-image'
            key={`top-section-image-selector-image_${image.uuid}_${i}`}
          >
            <ResponsiveImage file={image} />
            <Button
              withIcon
              danger
              className='text-white'
              onClick={() => {
                const tempImages = [...content.data.images]
                tempImages.splice(i, 1)
                const e: ChangeEvent<FileEntryBM[]> = {
                  target: {
                    name: 'images',
                    value: tempImages,
                  },
                }
                setData(e)
              }}
            >
              <Icon name={LucideIconName.X} />
            </Button>
          </div>
        ))}
        <Button
          className='mbottom1'
          onClick={() => {
            openMediaSelector({
              fileType: 'image',
              onSelect: (f) => {
                if (!f) return closeMediaSelector()
                const tempImages = [...content.data.images]
                tempImages.push(f)
                const e = {
                  target: {
                    name: 'images',
                    value: tempImages,
                  },
                }
                setData(e)
                closeMediaSelector()
              },
            })
          }}
        >
          {tr(TrKey.topSectionEditorImageCarouselAddImage)}
        </Button>
      </RightBarGroup>

      <Select
        light
        label={tr(TrKey.topSectionEditorHeightLabel)}
        desc={tr(TrKey.topSectionEditorHeightDescription)}
        id='minHeight'
        name='minHeight'
        value={content.data.minHeight || ''}
        onChange={setData}
      >
        <option value='' disabled>
          {tr(TrKey.topSectionEditorHeightPlaceholder)}
        </option>
        <option value='30vh'>
          {tr(TrKey.topSectionEditorHeightOptionLow)}
        </option>
        <option value='40vh'>
          {tr(TrKey.topSectionEditorHeightOptionMedium)}
        </option>
        <option value='50vh'>
          {tr(TrKey.topSectionEditorHeightOptionHigh)}
        </option>
      </Select>
      <Select
        light
        label={tr(TrKey.topSectionEditorMarginBottomLabel)}
        desc={tr(TrKey.topSectionEditorMarginBottomDescription)}
        id='marginBottom'
        name='marginBottom'
        value={content.data.marginBottom}
        onChange={setData}
      >
        <option value='' disabled>
          {tr(TrKey.topSectionEditorMarginBottomPlaceholder)}
        </option>
        {marginOptions.map((opt) => (
          <option key={`margin_option_${opt.value}`} value={opt.value}>
            {opt.name}
          </option>
        ))}
      </Select>
    </>
  )
}

export default TopSectionEditor
